import type {
	ExternalTestArtifactDetailResponse,
	ExternalTestRunRequestDraftResponse,
	ExternalTestsOverviewResponse,
} from "@/lib/api/external-tests";

import { registerFixture } from "./registry";

const overview: ExternalTestsOverviewResponse = {
	scope: "tau2_retail_external_tests",
	domain: "retail",
	current_phase: "p0_closure_signed_external_test_ready",
	ledger_status: "active_current_state",
	external_testing_decision: "adapter_only_r4_native_poc_allowed",
	can_start_external_tests: true,
	admin_execution_enabled: false,
	evaluation_criteria: [
		{
			id: "task_mapping",
			label: "任务可映射",
			question: "tau2 retail task 能否映射到能力包、方案和 Skill。",
			tau2_signal: "task action、policy、user goal",
			internal_presentation: "capability package、target playbook、test mode",
		},
		{
			id: "evidence_explainability",
			label: "证据链可解释",
			question: "订单、商品、政策等证据是否能解释 Agent 的回复和动作。",
			tau2_signal: "tool call、trajectory、assertion",
			internal_presentation: "evidence refs、read-only evidence、summary",
		},
		{
			id: "write_protection",
			label: "写操作被保护",
			question: "取消、退货、改地址等高风险动作是否只进入 dry-run 或 sandbox。",
			tau2_signal: "action type、DB/action assertion",
			internal_presentation: "protected write dry-run、confirmation gate",
		},
		{
			id: "failure_attribution",
			label: "结果可归因",
			question: "失败能否归因到能力缺口、adapter 映射或上游任务差异。",
			tau2_signal: "reward breakdown、failed assertion、run log",
			internal_presentation: "capability backlog、adapter issue、manual review",
		},
		{
			id: "boundary_control",
			label: "边界不突破",
			question: "外部测试是否不污染主应答、内部评测和真实 Shopify。",
			tau2_signal: "domain/run artifact、sandbox policy",
			internal_presentation: "no live write、no EvalRun auto writeback、no schema change",
		},
	],
	task_sets: [
		{
			id: "p0_supported_probe",
			label: "P0 支持探针",
			description: "优先验证已有 PB/SK 可承接的订单、商品、知识、取消、退货、改地址、转人工能力。",
			task_count: 8,
			expected_statuses: ["supported_contract", "supported_with_identity_mock_and_dry_run"],
			test_modes: ["contract_only", "read_only_and_protected_write_dry_run"],
			primary_questions_preview: [
				"订单、商品和退货能力包是否能形成可解释 dry-run。",
				"邮箱定位、订单详情和退货链路是否能保持证据和确认边界。",
				"取消和退货组合任务是否能区分方案分支并输出金额/确认 diagnostics。",
				"订单配送地址修改是否经过订单状态检查、地址槽位检查和确认链。",
			],
		},
		{
			id: "p1_gap_probe",
			label: "P1 缺口探针",
			description: "用于验证系统是否能清楚表达缺口、转人工和后续能力设计方向。",
			task_count: 9,
			expected_statuses: ["gap_diagnostic"],
			test_modes: ["unsupported_diagnostic"],
			primary_questions_preview: [
				"换货能力是否需要新增方案或扩展 PB03。",
				"改商品时是否能识别库存、差价和支付偏好缺口。",
				"金额差异计算和订单修改方案是否需要共同设计。",
			],
		},
	],
	test_layers: [
		{
			test_layer: "adapter_only_static_mapping",
			label: "静态映射检查",
			phase: "post_signoff_adapter_only",
			mode: "adapter_only_static",
			domain: "retail",
			artifact_policy: "console_summary_only",
			status: "artifact_valid",
			required_files_count: 4,
			latest_artifact: {
				run_id: "tau2_adapter_only_static_mapping_20260521_001",
				test_layer: "adapter_only_static_mapping",
				created_at: "2026-05-21T08:12:31+08:00",
				artifact_dir:
					"artifacts/external-evaluation/tau2-bench/tau2_adapter_only_static_mapping_20260521_001",
				command: "uv run python tools/external-evaluation/tau2_adapter/validate_static_mapping.py",
				command_returncode: 0,
				readiness_status: "external_tests_ready",
				static_contract_valid: true,
				human_interpretation_required: true,
				tau2_commit: "6899b47c910d38750d23d363460f67938d8c3dfc",
				project_commit: "54c77160a16111ba2d0fc62b6eaecebf521190b1",
				missing_required_files: [],
			},
			can_execute_from_admin: false,
			requires_manual_confirmation: false,
			next_action: "已具备只读证据，可进入人工解释和下一层判断。",
		},
		{
			test_layer: "r4_read_only_protected_dry_run_contract_test",
			label: "R4 只读 / dry-run 合同测试",
			phase: "post_signoff_r4_contract",
			mode: "fake_contract",
			domain: "retail",
			artifact_policy: "fake_contract_diagnostics_only",
			status: "artifact_valid",
			required_files_count: 6,
			latest_artifact: {
				run_id: "tau2_r4_read_only_protected_dry_run_contract_test_20260521_001",
				test_layer: "r4_read_only_protected_dry_run_contract_test",
				created_at: "2026-05-21T08:12:31+08:00",
				artifact_dir:
					"artifacts/external-evaluation/tau2-bench/tau2_r4_read_only_protected_dry_run_contract_test_20260521_001",
				command: "uv run python tools/external-evaluation/tau2_adapter/run_contract_suite.py",
				command_returncode: 0,
				readiness_status: "external_tests_ready",
				static_contract_valid: true,
				human_interpretation_required: true,
				tau2_commit: "6899b47c910d38750d23d363460f67938d8c3dfc",
				project_commit: "54c77160a16111ba2d0fc62b6eaecebf521190b1",
				missing_required_files: [],
			},
			can_execute_from_admin: false,
			requires_manual_confirmation: false,
			next_action: "已具备只读证据，可进入人工解释和下一层判断。",
		},
		{
			test_layer: "tau2_native_poc_preflight",
			label: "Native PoC 预检",
			phase: "post_signoff_native_preflight",
			mode: "preflight_only",
			domain: "retail",
			artifact_policy: "preflight_summary_only",
			status: "artifact_valid",
			required_files_count: 5,
			latest_artifact: {
				run_id: "tau2_native_poc_preflight_20260521_001",
				test_layer: "tau2_native_poc_preflight",
				created_at: "2026-05-21T08:12:31+08:00",
				artifact_dir:
					"artifacts/external-evaluation/tau2-bench/tau2_native_poc_preflight_20260521_001",
				command: "uv run python tools/external-evaluation/tau2_adapter/native_poc_preflight.py",
				command_returncode: 0,
				readiness_status: "external_tests_ready",
				static_contract_valid: true,
				human_interpretation_required: true,
				tau2_commit: "6899b47c910d38750d23d363460f67938d8c3dfc",
				project_commit: "54c77160a16111ba2d0fc62b6eaecebf521190b1",
				missing_required_files: [],
			},
			can_execute_from_admin: false,
			requires_manual_confirmation: false,
			next_action: "已具备只读证据，可进入人工解释和下一层判断。",
		},
		{
			test_layer: "offline_tau2_retail_poc",
			label: "离线零售 PoC",
			phase: "post_signoff_offline_poc",
			mode: "native_offline",
			domain: "retail",
			artifact_policy: "external_benchmark_artifacts_only",
			status: "artifact_valid",
			required_files_count: 14,
			latest_artifact: {
				run_id: "tau2_offline_tau2_retail_poc_20260521_002",
				test_layer: "offline_tau2_retail_poc",
				created_at: "2026-05-21T19:35:00+08:00",
				artifact_dir:
					"artifacts/external-evaluation/tau2-bench/tau2_offline_tau2_retail_poc_20260521_002",
				command:
					"uv run tau2 run --domain retail --agent llm_agent --agent-llm openrouter/openai/gpt-4.1 --user-llm openrouter/openai/gpt-4.1 --task-ids 67 68 25 50 --num-trials 1 --max-concurrency 1 --seed 300 --save-to tau2_poc_retail_native_20260520_001__tau2_offline_tau2_retail_poc_20260521_002",
				command_returncode: 0,
				readiness_status: "external_tests_ready",
				static_contract_valid: true,
				human_interpretation_required: true,
				tau2_commit: "6899b47c910d38750d23d363460f67938d8c3dfc",
				project_commit: "060559d",
				missing_required_files: [],
			},
			can_execute_from_admin: false,
			requires_manual_confirmation: true,
			next_action: "第一轮真实效果测试已完成，下一步进入人工解读和 adapter 映射判断。",
		},
	],
	latest_runs: [
		{
			run_id: "tau2_offline_tau2_retail_poc_20260521_002",
			test_layer: "offline_tau2_retail_poc",
			created_at: "2026-05-21T19:35:00+08:00",
			artifact_dir:
				"artifacts/external-evaluation/tau2-bench/tau2_offline_tau2_retail_poc_20260521_002",
			command:
				"uv run tau2 run --domain retail --agent llm_agent --agent-llm openrouter/openai/gpt-4.1 --user-llm openrouter/openai/gpt-4.1 --task-ids 67 68 25 50 --num-trials 1 --max-concurrency 1 --seed 300 --save-to tau2_poc_retail_native_20260520_001__tau2_offline_tau2_retail_poc_20260521_002",
			command_returncode: 0,
			readiness_status: "external_tests_ready",
			static_contract_valid: true,
			human_interpretation_required: true,
			tau2_commit: "6899b47c910d38750d23d363460f67938d8c3dfc",
			project_commit: "060559d",
			missing_required_files: [],
		},
		{
			run_id: "tau2_r4_read_only_protected_dry_run_contract_test_20260521_001",
			test_layer: "r4_read_only_protected_dry_run_contract_test",
			created_at: "2026-05-21T08:12:31+08:00",
			artifact_dir:
				"artifacts/external-evaluation/tau2-bench/tau2_r4_read_only_protected_dry_run_contract_test_20260521_001",
			command: "uv run python tools/external-evaluation/tau2_adapter/run_contract_suite.py",
			command_returncode: 0,
			readiness_status: "external_tests_ready",
			static_contract_valid: true,
			human_interpretation_required: true,
			tau2_commit: "6899b47c910d38750d23d363460f67938d8c3dfc",
			project_commit: "54c77160a16111ba2d0fc62b6eaecebf521190b1",
			missing_required_files: [],
		},
		{
			run_id: "tau2_native_poc_preflight_20260521_001",
			test_layer: "tau2_native_poc_preflight",
			created_at: "2026-05-21T08:12:31+08:00",
			artifact_dir:
				"artifacts/external-evaluation/tau2-bench/tau2_native_poc_preflight_20260521_001",
			command: "uv run python tools/external-evaluation/tau2_adapter/native_poc_preflight.py",
			command_returncode: 0,
			readiness_status: "external_tests_ready",
			static_contract_valid: true,
			human_interpretation_required: true,
			tau2_commit: "6899b47c910d38750d23d363460f67938d8c3dfc",
			project_commit: "54c77160a16111ba2d0fc62b6eaecebf521190b1",
			missing_required_files: [],
		},
		{
			run_id: "tau2_adapter_only_static_mapping_20260521_001",
			test_layer: "adapter_only_static_mapping",
			created_at: "2026-05-21T08:12:31+08:00",
			artifact_dir:
				"artifacts/external-evaluation/tau2-bench/tau2_adapter_only_static_mapping_20260521_001",
			command: "uv run python tools/external-evaluation/tau2_adapter/validate_static_mapping.py",
			command_returncode: 0,
			readiness_status: "external_tests_ready",
			static_contract_valid: true,
			human_interpretation_required: true,
			tau2_commit: "6899b47c910d38750d23d363460f67938d8c3dfc",
			project_commit: "54c77160a16111ba2d0fc62b6eaecebf521190b1",
			missing_required_files: [],
		},
	],
	feedback_routes: [
		{
			route: "capability_backlog",
			target: "docs/retail-capability-enhancement/",
			condition: "capability_gap_or_runtime_backlog",
		},
		{
			route: "adapter_mapping",
			target: "docs/external-evaluation/tau2-bench/",
			condition: "mapping_or_external_test_issue",
		},
	],
	isolation_strategy: [
		{
			id: "execution_isolation",
			label: "执行隔离",
			purpose: "把真实效果测试放在旁路执行，不让 Admin 页面变成 tau2 调度器。",
			protected_surface: "Admin 页面、后台任务队列、现有应答主链路",
			allowed_path: "人工确认后由外挂 runner 或命令行 runbook 执行，并只产出 artifact。",
			prohibited_actions: [
				"从 Admin 页面直接执行 tau2 run",
				"自动排队后台任务",
				"接入生产请求链路",
			],
		},
		{
			id: "data_isolation",
			label: "数据隔离",
			purpose: "验证零售客服能力时保护真实订单、客户和商家数据。",
			protected_surface: "真实 Shopify、业务数据库、生产 merchant 配置",
			allowed_path: "使用 tau2 sandbox、fake client、fixture 或影子数据承接读写动作。",
			prohibited_actions: ["真实 Shopify mutation", "写业务数据库", "把测试数据混入生产商家数据"],
		},
		{
			id: "tool_isolation",
			label: "工具隔离",
			purpose: "让 tau2 action 先映射到能力和 dry-run 合同，避免直接调用生产工具。",
			protected_surface: "生产工具调用、Phase4 写执行器、发布中的工具模式",
			allowed_path:
				"通过能力映射适配器连接 read-only evidence、protected dry-run 和 handoff 合同。",
			prohibited_actions: [
				"tau2 action 直连生产写工具",
				"绕过确认链",
				"把外部字段强行改成内部字段",
			],
		},
		{
			id: "result_isolation",
			label: "结果隔离",
			purpose: "把 tau2 结果保留为外部 benchmark 证据，而不是内部质量门禁。",
			protected_surface: "内部 EvalRun、Badcase、Regression、质量门禁",
			allowed_path: "先归档 external artifact，再由产品、评测和研发人工解释。",
			prohibited_actions: ["不自动写 EvalRun", "不自动创建 Badcase", "不自动写回 Regression"],
		},
		{
			id: "feedback_isolation",
			label: "回流隔离",
			purpose: "只把人工确认后的结论带回能力建设，避免外部测试结果自动改动现有体系。",
			protected_surface: "能力补齐文档、内部评测样本、主应答方案",
			allowed_path: "人工转写为能力 backlog、adapter 修正项或内部评测候选草案。",
			prohibited_actions: [
				"自动覆盖能力文档",
				"自动生成内部样本",
				"把 tau2 reward 当内部通过/失败",
			],
		},
	],
	boundaries: {
		admin_scope: "read_only_status_and_artifact_view",
		allowed_now: [
			"adapter_only_static_mapping",
			"r4_read_only_protected_dry_run_contract_test",
			"tau2_native_poc_preflight",
			"offline_tau2_retail_poc",
		],
		blocked_now: [
			"real_agent_runtime_integration",
			"db_schema_change",
			"live_shopify_write",
			"auto_evalrun_writeback",
		],
		still_prohibited: [
			"live_shopify_write",
			"real_agent_runtime_integration",
			"db_schema_change",
			"auto_evalrun_writeback",
			"auto_badcase_creation",
			"auto_regression_creation",
		],
		must_not: [
			"run_tau2_from_artifact_planner",
			"call_project_agent_runtime",
			"write_evalrun_or_badcase",
		],
	},
	source_documents: [
		"p0-closure-state-ledger.yaml",
		"external-run-artifact-plan.yaml",
		"retail-task-set.yaml",
	],
	updated_at: "2026-05-21",
};

function fixtureRun(index: number) {
	const run = overview.latest_runs[index];
	if (!run) {
		throw new Error(`missing external test fixture run at index ${index}`);
	}
	return run;
}

const r4ArtifactDetail: ExternalTestArtifactDetailResponse = {
	run: fixtureRun(1),
	summary_text:
		"# r4_read_only_protected_dry_run_contract_test Summary\n\n- run_id: `tau2_r4_read_only_protected_dry_run_contract_test_20260521_001`\n- command: `executed`\n- runtime impact: none\n",
	decision_text:
		"# r4_read_only_protected_dry_run_contract_test Decision\n\n本层命令已执行并返回 0，可进入人工复核。\n",
	command_outputs: [
		{
			path: "command-output/run_contract_suite.txt",
			preview:
				"contract_total=8\ncontract_passed=8\ncontract_failed=0\nread_only_contract=4\nprotected_dry_run_contract=4\nok fake contract suite valid\n",
			truncated: false,
		},
	],
	files: [
		{ path: "metadata.json", kind: "metadata", size_bytes: 900 },
		{
			path: "command-output/run_contract_suite.txt",
			kind: "command_output",
			size_bytes: 220,
		},
		{
			path: "interpretation/summary.zh-CN.md",
			kind: "interpretation",
			size_bytes: 260,
		},
		{
			path: "interpretation/decision.zh-CN.md",
			kind: "interpretation",
			size_bytes: 160,
		},
		{ path: "adapter/diagnostics.json", kind: "adapter", size_bytes: 80 },
	],
	admin_execution_enabled: false,
	can_write_internal_evaluation: false,
};

const artifactDetailsByRunId: Record<string, ExternalTestArtifactDetailResponse> = {
	tau2_offline_tau2_retail_poc_20260521_002: {
		...r4ArtifactDetail,
		run: fixtureRun(0),
		summary_text:
			"# tau2 offline retail PoC Summary\n\n| task | reward | termination |\n| --- | --- | --- |\n| `25` | `1.0` | `user_stop` |\n| `50` | `1.0` | `user_stop` |\n| `67` | `1.0` | `user_stop` |\n| `68` | `1.0` | `user_stop` |\n",
		decision_text:
			"# tau2 offline retail PoC Decision\n\n本 artifact 可作为 S1 第一轮真实效果测试的有效结果进入人工复核。\n",
		command_outputs: [
			{
				path: "command-output/run_isolated_native_poc.txt",
				preview:
					"execution_ready=true\nartifact_valid=true\nexecuted.tau2_poc_retail_native_20260520_001.returncode=0\n",
				truncated: false,
			},
		],
		files: [
			{ path: "metadata.json", kind: "metadata", size_bytes: 1200 },
			{ path: "raw/results.json", kind: "raw_result", size_bytes: 213802 },
			{ path: "raw/run.log", kind: "raw_log", size_bytes: 49889 },
			{
				path: "command-output/run_isolated_native_poc.txt",
				kind: "command_output",
				size_bytes: 240,
			},
			{
				path: "interpretation/summary.zh-CN.md",
				kind: "interpretation",
				size_bytes: 620,
			},
			{
				path: "interpretation/decision.zh-CN.md",
				kind: "interpretation",
				size_bytes: 260,
			},
		],
	},
	tau2_r4_read_only_protected_dry_run_contract_test_20260521_001: r4ArtifactDetail,
	tau2_native_poc_preflight_20260521_001: {
		...r4ArtifactDetail,
		run: fixtureRun(2),
		summary_text: "# tau2_native_poc_preflight Summary\n\n- command: `executed`\n",
		decision_text: "# tau2_native_poc_preflight Decision\n\n本层命令已执行并返回 0。\n",
		command_outputs: [
			{
				path: "command-output/native_poc_preflight.txt",
				preview: "preflight_passed=true\nok native poc preflight valid\n",
				truncated: false,
			},
		],
		files: [
			{ path: "metadata.json", kind: "metadata", size_bytes: 900 },
			{
				path: "command-output/native_poc_preflight.txt",
				kind: "command_output",
				size_bytes: 120,
			},
			{
				path: "interpretation/summary.zh-CN.md",
				kind: "interpretation",
				size_bytes: 260,
			},
			{
				path: "interpretation/decision.zh-CN.md",
				kind: "interpretation",
				size_bytes: 160,
			},
		],
	},
	tau2_adapter_only_static_mapping_20260521_001: {
		...r4ArtifactDetail,
		run: fixtureRun(3),
		summary_text: "# adapter_only_static_mapping Summary\n\n- command: `executed`\n",
		decision_text: "# adapter_only_static_mapping Decision\n\n本层命令已执行并返回 0。\n",
		command_outputs: [
			{
				path: "command-output/validate_static_mapping.txt",
				preview: "readiness_status=external_tests_ready\nok static mapping valid\n",
				truncated: false,
			},
		],
		files: [
			{ path: "metadata.json", kind: "metadata", size_bytes: 900 },
			{
				path: "command-output/validate_static_mapping.txt",
				kind: "command_output",
				size_bytes: 180,
			},
			{
				path: "interpretation/summary.zh-CN.md",
				kind: "interpretation",
				size_bytes: 260,
			},
			{
				path: "interpretation/decision.zh-CN.md",
				kind: "interpretation",
				size_bytes: 160,
			},
		],
	},
};

const runRequestDraft: ExternalTestRunRequestDraftResponse = {
	draft_status: "manual_review_required",
	task_set_id: "p0_supported_probe",
	test_layer: "offline_tau2_retail_poc",
	objective: "验证 P0 支持探针是否可进入小样本 tau2 retail PoC。",
	admin_execution_enabled: false,
	ready_for_admin_execution: false,
	required_confirmations: [
		"确认 LLM 成本预算",
		"确认 tau2 checkout、任务包和模型配置",
		"确认 artifact 归档目录和人工解读责任人",
		"确认不自动写 EvalRun、Badcase 或 Regression",
	],
	blocked_actions: [
		"不调用项目真实 Agent runtime",
		"不执行 Shopify live write",
		"不自动写 EvalRun、Badcase、Regression",
		"不从 Admin 页面执行 tau2 run",
	],
	expected_outputs: [
		"raw/results.json",
		"metadata.json",
		"command-output/*.txt",
		"interpretation/summary.zh-CN.md",
		"interpretation/decision.zh-CN.md",
	],
	handoff_documents: [
		"docs/external-evaluation/tau2-bench/offline-poc-runbook.zh-CN.md",
		"docs/external-evaluation/tau2-bench/external-artifact-contract.zh-CN.md",
		"docs/admin-console-productization/external-tests/test-operation-guide.zh-CN.md",
	],
	readiness_checks: [
		{
			id: "llm_cost_budget",
			label: "LLM 成本预算",
			status: "passed",
			detail: "已确认预算",
		},
		{
			id: "tau2_checkout_and_model_config",
			label: "tau2 checkout 与模型配置",
			status: "manual_required",
			detail: "执行前需人工确认 tau2 commit、模型、seed 和并发。",
		},
		{
			id: "artifact_contract",
			label: "artifact 归档合同",
			status: "manual_required",
			detail: "执行后必须归档 raw result、metadata、命令输出和人工解读。",
		},
		{
			id: "project_agent_runtime",
			label: "项目真实 Agent runtime",
			status: "passed",
			detail: "草案固定不调用项目真实 Agent runtime。",
		},
		{
			id: "shopify_live_write",
			label: "Shopify live write",
			status: "passed",
			detail: "草案固定禁止 Shopify live write。",
		},
	],
	handoff_summary_markdown:
		"# tau2 retail 外挂测试人工申请草案\n\n- 任务集: `p0_supported_probe`\n- 测试层: `offline_tau2_retail_poc`\n- 草案状态: `manual_review_required`\n- Admin 执行: `false`\n\n## 隔离策略\n- 执行隔离：真实效果测试只进入外挂 runner 或命令行 runbook。\n- 数据隔离：零售任务使用 tau2 sandbox、fake client 或影子数据。\n\n## 禁止动作\n- 不从 Admin 页面执行 tau2 run\n- 不执行 Shopify live write\n\n## 预期输出物\n- raw/results.json",
};

export function registerExternalTestsFixtures(): void {
	registerFixture<ExternalTestsOverviewResponse>({
		pathname: "/admin/v1/external-tests/overview",
		methods: ["GET"],
		resolve: () => ({ data: overview }),
	});
	registerFixture<ExternalTestArtifactDetailResponse | { detail: string }>({
		pathname: "/admin/v1/external-tests/artifacts/:run_id",
		methods: ["GET"],
		resolve: ({ params }) => {
			const detail = artifactDetailsByRunId[params.run_id ?? ""];
			return detail
				? { data: detail }
				: { status: 404, data: { detail: "external test artifact not found" } };
		},
	});
	registerFixture<ExternalTestRunRequestDraftResponse>({
		pathname: "/admin/v1/external-tests/run-requests/draft",
		methods: ["POST"],
		resolve: () => ({ data: runRequestDraft }),
	});
}
